import { DEFAULT_WEEKDAYS, endOfWeekDay, isWorkingDay, shiftDay, startOfWeekDay } from "../date";

/** What a ticket's stored dates mean, once `dateMode` has been read. */
export interface TicketSpan {
	/** The first day covered. */
	start: string;
	/** The last day covered. */
	end: string;
	/** Whether the dates name calendar weeks rather than days. */
	isWeekMode: boolean;
	/**
	 * The weekdays work happens on at this ticket's site, as its project defines them.
	 *
	 * It rides on the span because every resolver here needs it and none of them would otherwise
	 * know the project: what counts as a working day is a decision about the site, so a ticket on a
	 * Saturday-working site draws six columns a week and one beside it draws five.
	 */
	weekdays: number[];
	/**
	 * The days this ticket's work cannot happen on, whatever the week says: no access, a delivery,
	 * an inspection. One-offs, so they sit on the ticket rather than on the site.
	 */
	excludedDates: string[];
}

/** The three columns a ticket's schedule is read from. */
export interface TicketDates {
	startDate: string;
	endDate: string;
	dateMode: "date" | "week" | null;
	/** The project's own weekdays. Absent falls back to Monday to Friday. */
	weekdays?: number[] | null;
	/** The days a break has been put on. Absent is none. */
	excludedDates?: string[] | null;
}

/**
 * The days a ticket's schedule actually covers.
 *
 * **A `week` ticket's date names a week, not a day.** Its start widens to that week's Monday and
 * its end to that week's Sunday, which is the span the ticket card already reads it as. Drawing the
 * stored day alone puts a week's work on one column.
 *
 * Every ticket has a span: `tickets.start_date` and `tickets.end_date` are NOT NULL, so there is no
 * unscheduled case to answer for. A one-day ticket carries the same date at both ends.
 *
 * @param ticket - The ticket's two dates and its mode.
 * @returns The covered span.
 */
export function resolveTicketSpan(ticket: TicketDates): TicketSpan {
	const isWeekMode = ticket.dateMode === "week";
	return {
		start: isWeekMode ? startOfWeekDay(ticket.startDate) : ticket.startDate,
		end: isWeekMode ? endOfWeekDay(ticket.endDate) : ticket.endDate,
		isWeekMode,
		weekdays: ticket.weekdays?.length ? ticket.weekdays : DEFAULT_WEEKDAYS,
		excludedDates: ticket.excludedDates ?? [],
	};
}

/**
 * Whether a ticket's work happens on a day: the site works that weekday, it is no holiday, and
 * nobody has put a break on it.
 *
 * Every walk here counts through this, so a break is subtracted from the estimate's ceiling, cut
 * out of the bar, skipped by a resize handle and passed over by the crew's own check without any
 * of them being told about breaks separately.
 *
 * @param span - The ticket's resolved window.
 * @param date - The day being tested, as a stored `YYYY-MM-DD` string.
 * @returns Whether the work can happen on it.
 */
export function isWorkedDay(span: TicketSpan, date: string): boolean {
	if (span.excludedDates.includes(date)) return false;
	return isWorkingDay(date, span.weekdays);
}

/**
 * Whether a day falls inside a ticket's span.
 *
 * @param span - The ticket's resolved span.
 * @param date - The day being tested, as a stored `YYYY-MM-DD` string.
 * @returns Whether the ticket covers that day.
 */
export function ticketSpanCovers(span: TicketSpan, date: string): boolean {
	return date >= span.start && date <= span.end;
}

/**
 * The last day the estimated work reaches, counting from the start of the ticket's window.
 *
 * **`estimated_days` counts working days**, which is what the field asks for — "wie viele
 * Arbeitstage das Ticket im Zeitfenster benötigt" — so the walk steps over weekends and holidays
 * and a three-day estimate beginning on a Friday ends on the Tuesday.
 *
 * The walk is bounded by the window: an estimate larger than the window holds is clamped to its
 * last day rather than drawn past the days the ticket was actually given. A ticket with no
 * estimate has no such day at all, and its bar is the window's own.
 *
 * @param span - The ticket's resolved window.
 * @param estimatedDays - The working days the ticket is estimated at, or null when nobody has said.
 * @param from - The day the work begins; the window's own start when it has not been placed.
 * @returns The last day the work covers, or null when there is no estimate to draw.
 */
export function resolveEstimateEnd(
	span: TicketSpan,
	estimatedDays: number | null,
	from: string = span.start,
): string | null {
	if (estimatedDays === null || estimatedDays <= 0) return null;

	let remaining = estimatedDays;
	for (let cursor = from; cursor <= span.end; cursor = shiftDay(cursor, 1)) {
		if (!isWorkedDay(span, cursor)) continue;
		remaining -= 1;
		if (remaining <= 0) return cursor;
	}

	// The window ran out first, so the work fills every day of it that anybody works.
	return span.end;
}

/**
 * The latest day the work can begin on and still finish inside the window.
 *
 * Counted backwards from the window's last day, so it is the mirror of `resolveEstimateEnd` and
 * lands on a working day by construction. A window too short for its own estimate has no such day,
 * and the work simply begins when the window does.
 *
 * @param span - The ticket's resolved window.
 * @param estimatedDays - The working days the ticket is estimated at.
 * @returns The last day the work may start on.
 */
export function resolveLatestWorkStart(span: TicketSpan, estimatedDays: number | null): string {
	if (estimatedDays === null || estimatedDays <= 0) return span.start;

	let remaining = estimatedDays;
	for (let cursor = span.end; cursor >= span.start; cursor = shiftDay(cursor, -1)) {
		if (!isWorkedDay(span, cursor)) continue;
		remaining -= 1;
		if (remaining <= 0) return cursor;
	}

	// The window cannot hold the whole estimate, so there is nowhere to slide it to.
	return span.start;
}

/**
 * The day a ticket's work actually begins: what was stored, held inside what is possible.
 *
 * A stored placement is clamped into the window and pushed onto a working day, because the window
 * can be edited after the bar was dragged and a Saturday start would draw a bar whose first day is
 * blank. Nothing stored means the work begins when the window does, which is what every ticket read
 * before one could be placed.
 *
 * @param span - The ticket's resolved window.
 * @param estimatedDays - The working days the ticket is estimated at.
 * @param stored - The placement on the row, or null.
 * @returns The day the work begins.
 */
export function resolveWorkStart(
	span: TicketSpan,
	estimatedDays: number | null,
	stored: string | null,
): string {
	const latest = resolveLatestWorkStart(span, estimatedDays);
	let candidate = stored ?? span.start;
	if (candidate < span.start) candidate = span.start;
	if (candidate > latest) candidate = latest;

	// Forward onto a working day, then back if that overshot what the window can hold.
	let cursor = candidate;
	while (cursor <= latest && !isWorkedDay(span, cursor)) {
		cursor = shiftDay(cursor, 1);
	}
	return cursor > latest ? latest : cursor;
}

/**
 * Where the work sits after one of its edges was dragged.
 *
 * The two edges are not symmetrical. Dragging the trailing edge changes only how long the work is,
 * so its start stays put. Dragging the leading edge holds the far end still and moves the start,
 * which means the estimate grows by however many **working** days were uncovered — a Monday edge
 * dragged back over a weekend picks up one day for three columns, not three.
 *
 * Both are held inside the ticket's own window and cannot cross each other: an edge dragged past
 * the other one leaves a single working day rather than an inverted span. The landing day is always
 * a working day, because the estimate counts them and a bar beginning on a Saturday would draw a
 * blank first column.
 *
 * @param span - The ticket's resolved window.
 * @param work - Where the work currently begins and ends.
 * @param edge - Which handle was dragged.
 * @param deltaDays - How many calendar columns it travelled; negative is earlier.
 * @returns The day the work begins and the working days it now takes.
 */
export function resizeWork(
	span: TicketSpan,
	work: { start: string; end: string },
	edge: "start" | "end",
	deltaDays: number,
): { workStart: string; estimatedDays: number } {
	if (edge === "end") {
		const dragged = clampDate(shiftDay(work.end, deltaDays), work.start, span.end);
		const end = previousWorkedDay(span, dragged, work.start);
		return { workStart: work.start, estimatedDays: workedDaysBetween(span, work.start, end) };
	}

	const dragged = clampDate(shiftDay(work.start, deltaDays), span.start, work.end);
	const start = nextWorkedDay(span, dragged, work.end);
	return { workStart: start, estimatedDays: workedDaysBetween(span, start, work.end) };
}

/** A stored date held between two others. */
function clampDate(date: string, min: string, max: string): string {
	if (date < min) return min;
	if (date > max) return max;
	return date;
}

/** The first worked day from a date forwards, falling back to the last one before the limit. */
function nextWorkedDay(span: TicketSpan, date: string, limit: string): string {
	let cursor = date;
	while (cursor <= limit && !isWorkedDay(span, cursor)) {
		cursor = shiftDay(cursor, 1);
	}
	return cursor > limit ? previousWorkedDay(span, limit, date) : cursor;
}

/** The first worked day from a date backwards, falling back to the date itself. */
function previousWorkedDay(span: TicketSpan, date: string, limit: string): string {
	let cursor = date;
	while (cursor >= limit && !isWorkedDay(span, cursor)) {
		cursor = shiftDay(cursor, -1);
	}
	return cursor < limit ? date : cursor;
}

/** The worked days a span holds, floored at one: an estimate of zero is not a ticket. */
function workedDaysBetween(span: TicketSpan, start: string, end: string): number {
	let count = 0;
	for (let cursor = start; cursor <= end; cursor = shiftDay(cursor, 1)) {
		if (isWorkedDay(span, cursor)) count += 1;
	}
	return Math.max(count, 1);
}

/**
 * How many worked days the whole window holds — the ceiling on a ticket's estimate.
 *
 * @param span - The ticket's resolved window.
 * @returns The days the work could occupy, floored at one so the field stays answerable.
 */
export function countWorkedDaysInSpan(span: TicketSpan): number {
	return workedDaysBetween(span, span.start, span.end);
}
